import { modifier } from 'ember-modifier';
import { waitForLater, waitForRender } from 'ember-simplepractice/utils/waiters';

const SCROLL_END_DEBOUNCE_MS = 120;

let nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

/**
 * Fits an element's max-height to the remaining vertical space in the viewport.
 *
 * Computes `window.innerHeight - element.getBoundingClientRect().top - bottomBuffer`
 * and applies it as inline `max-height`. Recomputes on window resize,
 * orientationchange, and scroll. After scroll momentum stops, waits one frame
 * for sticky layout to settle, then dispatches a synthetic window `resize` so
 * ember-basic-dropdown re-runs its calculatePosition.
 *
 * Usage:
 *   {{fit-to-viewport-height bottomBuffer=16}}
 *   {{fit-to-viewport-height min=240 max=624 bottomBuffer=24}}
 */
export default modifier(async function fitToViewportHeight(element, _, named = {}) {
  await waitForRender();
  let bottomBuffer = Number(named.bottomBuffer ?? 16);
  let min = Number(named.min ?? 0);
  let max = Number.isFinite(Number(named.max)) ? Number(named.max) : Infinity;

  let destroyed = false;
  let settleVersion = 0;

  let apply = () => {
    if (destroyed) return;
    let top = element.getBoundingClientRect().top;
    let available = Math.floor(window.innerHeight - top - bottomBuffer);
    let bounded = Math.min(max, Math.max(min, available));
    element.style.maxHeight = `${bounded}px`;
  };

  let runSettle = async myVersion => {
    await nextFrame();
    if (destroyed || myVersion !== settleVersion) return;
    apply();
    window.dispatchEvent(new Event('resize'));
  };

  let scheduleSettleWithDebounce = async () => {
    let myVersion = ++settleVersion;
    await waitForLater(SCROLL_END_DEBOUNCE_MS);
    if (destroyed || myVersion !== settleVersion) return;
    await runSettle(myVersion);
  };

  let scheduleSettleNow = () => {
    let myVersion = ++settleVersion;
    runSettle(myVersion);
  };

  let onScroll = () => {
    apply();
    scheduleSettleWithDebounce();
  };

  let onScrollEnd = () => {
    scheduleSettleNow();
  };

  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scrollend', onScrollEnd);

  return () => {
    destroyed = true;
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('scrollend', onScrollEnd);
  };
});
